import React from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  IconButton,
  useTheme,
  useMediaQuery,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { DataGrid, GridColDef } from "@mui/x-data-grid";
import { IAddressModel, ITransactionInfoModel } from "../../types/types";
import { useNavigate } from "react-router-dom";
import dayjs, { Dayjs } from "dayjs";
import { useSelector } from "react-redux";
import { RootState } from "../../store/reducers";

interface TransactionsDialogProps {
  open: boolean;
  onClose: () => void;
  transactions: ITransactionInfoModel[];
  title: string;
  loading: boolean;
}

const TransactionsDialog: React.FC<TransactionsDialogProps> = ({
  open,
  onClose,
  transactions,
  title,
  loading,
}) => {
  const theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down("md"));
  const navigate = useNavigate();
  const { data: userData } = useSelector(
    (state: RootState) => state.commonData.userData
  );
  function getTypeDecription(typeId: number | undefined): React.ReactNode {
    const result = userData?.transactionTypes?.find((x) => x.id === typeId);
    if (result) {
      return result.name;
    }
  }
  const columns: GridColDef[] = [
    {
      field: "date",
      headerName: "Date",
      minWidth: 120,
      flex: 1,
      valueFormatter: (params: Dayjs) => params?.format("MMM/DD/YYYY") || "",
    },
    {
      field: "typeId",
      headerName: "Type",
      minWidth: 120,
      flex: 1,
      valueFormatter: (params: number) => getTypeDecription(params),
    },
    {
      field: "address",
      headerName: "Address",
      minWidth: 200,
      flex: 2,
      valueFormatter: (params: IAddressModel) => params?.formattedAddress || "",
    },
    {
      field: "price",
      headerName: "Price",
      minWidth: 120,
      flex: 1,
      type: "number",
      valueFormatter: (params: number) => {
        if (params === null || params === undefined) return "";
        return `$${params.toLocaleString(undefined, {
          minimumFractionDigits: 0,
          maximumFractionDigits: 0,
        })}`;
      },
    },
  ];

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullScreen={fullScreen}
      maxWidth="md"
      fullWidth
    >
      <DialogTitle>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Typography variant="h6">{title}</Typography>
          <IconButton
            edge="end"
            color="inherit"
            onClick={onClose}
            aria-label="close"
          >
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>
      <DialogContent dividers>
        {transactions.length === 0 && !loading ? (
          <Typography variant="body1" align="center" sx={{ py: 4 }}>
            No transactions found.
          </Typography>
        ) : (
          <DataGrid
            rows={transactions}
            columns={columns}
            autoHeight
            density="standard"
            disableColumnSelector
            loading={loading}
            onRowClick={(params) => navigate(`/transactions/view/${params.id}`)}
            sx={{
              "& .MuiDataGrid-row": {
                cursor: "pointer",
                "&:hover": {
                  backgroundColor: "action.hover",
                },
              },
            }}
            initialState={{
              pagination: {
                paginationModel: { pageSize: 20 },
              },
            }}
          />
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} color="primary">
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default TransactionsDialog;
